import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=abstract%20dark%20background%20with%20golden%20amber%20light%20rays%2C%20geometric%20patterns%2C%20digital%20identity%20technology%20concept%2C%20warm%20tones%2C%20no%20text%20no%20logos%2C%20cinematic%20moody%20atmosphere&width=900&height=1200&seq=loginbg&orientation=portrait)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-brand-900/40" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="flex items-center gap-2.5 mb-12">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-600">
                <i className="ri-shield-check-line text-white text-lg" />
              </div>
              <span className="font-bold text-xl tracking-tight">
                eKYC Platform
              </span>
            </div>

            <h2 className="text-3xl font-bold mb-4 leading-tight">
              Enterprise Identity
              <br />
              Verification, Simplified
            </h2>
            <p className="text-white/70 text-sm leading-relaxed max-w-[360px]">
              Multi-tenant SaaS platform for businesses to onboard, verify,
              and manage customer identities securely and at scale.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {[
              { icon: 'ri-shield-check-line', text: 'SOC 2 Certified' },
              { icon: 'ri-lock-line', text: 'End-to-End Encryption' },
              { icon: 'ri-global-line', text: '99.9% Uptime SLA' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3">
                <i className={`${item.icon} text-brand-400 text-lg`} />
                <span className="text-white/80 text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-[420px]">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-600">
              <i className="ri-shield-check-line text-white text-lg" />
            </div>
            <span className="font-bold text-lg text-stone-900">
              eKYC Platform
            </span>
          </div>

          <h1 className="text-2xl font-bold text-stone-900 mb-2">
            Welcome back
          </h1>
          <p className="text-stone-500 text-sm mb-8">
            Sign in to your account to manage your eKYC operations.
          </p>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500 text-sm" />
              <span className="text-red-600 text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-stone-700 text-sm font-medium mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-stone-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 transition-colors"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-stone-700 text-sm font-medium mb-1.5">
                Password
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-stone-200 rounded-lg pl-10 pr-10 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 transition-colors"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  <i
                    className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} text-sm`}
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-stone-600 text-sm">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-brand-600 text-sm font-medium hover:text-brand-700"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={submitting || isLoading}
              className="w-full bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold py-3 rounded-full transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {submitting || isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Signing in...
                </>
              ) : (
                <>Sign In</>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-stone-100">
            <p className="text-stone-400 text-xs text-center">
              By signing in, you agree to our{' '}
              <span className="text-stone-500">Terms of Service</span>{' '}
              and{' '}
              <span className="text-stone-500">Privacy Policy</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
